import React, { useState, useContext, useMemo } from "react";
import { Formik } from "formik";
import _ from "lodash";
import { UploadOutlined } from "@ant-design/icons";
import { Table } from "formik-antd";
import { Button, message, Row, Col, Upload, Modal } from "antd";
import MultiStepFormContext from "./MultiStepFormContext";
import { removeFileFromSDK, uploadFileToSDK } from "../../utils/sdk";
import axios from "axios";
import { Backdrop, CircularProgress } from "@mui/material";
import useDatasource from "./datasource";

export default function Documents() {
  const { next, details } = useContext(MultiStepFormContext);
  const [loading, setLoading] = useState(false);
  const { dataSource } = useDatasource();
  const [errors, setErrors] = useState([]);

  const listRender = (document_id, path) => {
    if (!path) return [];
    else
      return [
        {
          uid: "-1",
          name: document_id,
          status: "done",
          url: path,
        },
      ];
  };
  const { documents, setDocuments } = useContext(MultiStepFormContext);

  const application_id = sessionStorage.getItem("applicationId");

  const [fileList, setFileList] = useState({});

  useMemo(() => {
    setFileList(documents);
  }, [documents]);

  async function checkUpload(file) {
    const isPDF = file.type === "application/pdf";
    const isSizeOK = file.size <= 1048576;

    if (!isPDF) {
      message.error("You can only upload PDF Files! ");
    } else if (!isSizeOK) {
      message.error("File must be smaller than 1 MB!");
    }

    return isPDF && isSizeOK;
  }

  async function handleUpload(document_id, option) {
    setLoading(true);
    const { onSuccess, onError, file } = option;

    if (!application_id) {
      message.error("Application ID is undefined. Please contact admin.");
      onError("Application ID is undefined.");
      setLoading(false);
      return;
    }

    try {
      const res = await uploadFileToSDK(file, document_id, application_id);
      if (!res || !res.Location) {
        throw new Error("File upload to S3 failed. No location returned.");
      }

      const url = window.API_PREFIX + "forms/api/post/documents";
      const requestOptions = {
        applicationId: application_id,
        documentType: document_id,
        documentPath: res.Location,
      };

      const result = await axios.post(url, requestOptions);
      if (result.status !== 200) {
        throw new Error(`Backend error: ${result.statusText}`);
      }

      setFileList({ ...fileList, [document_id]: res.Location });
      onSuccess("File uploaded successfully!");
      setDocuments({ ...fileList, [document_id]: res.Location });
      message.success("File uploaded successfully!");
    } catch (error) {
      console.error("Error during file upload:", error);
      message.error(
        `Something went wrong while uploading the file: ${error.message}. Please try again later.`
      );
      onError(`File upload failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  function onFinish(values) {
    next();
  }

  async function handleDelete(file, document_id) {
    setLoading(true);
    if (!fileList[document_id]) return true;
    return new Promise((resolve, reject) => {
      return Modal.confirm({
        title: "Are you sure you want to delete this file.?",
        onOk: async () => {
          try {
            const res = await removeFileFromSDK(
              file,
              document_id,
              application_id
            );
            const url = window.API_PREFIX + "forms/api/post/delete_documents";
            const requestOptions = {
              applicationId: application_id,
              documentType: document_id,
            };
            const result = await axios.post(url, requestOptions);
            setFileList((prev) => {
              const updatedObj = { ...prev };
              delete updatedObj[document_id];
              return updatedObj;
            });
            message.info("File removed successfully!");
            setDocuments((prev) => {
              const updatedObj = { ...prev };
              delete updatedObj[document_id];
              return updatedObj;
            });
            setLoading(false);
            resolve(true);
          } catch (error) {
            setLoading(false);
            reject(error);
          }
        },
        onCancel: () => {
          resolve(false);
        },
      });
    });
  }

  const checkValidation = () => {
    const errors = [];

    dataSource.forEach((item) => {
      if (item.required && !fileList[item.document_id]) {
        errors.push({ field: item.document_id, message: item.document_title });
      }
    });

    if (errors.length > 0) {
      setErrors(errors);
      const errorMessage = (
        <div>
          <p>Please upload the below required documents.</p>
          <ul
            style={{
              textAlign: "left",
              display: "flex",
              flexDirection: "column",
              gap: ".625rem",
            }}
          >
            {errors.map((error) => (
              <li key={error.field}>
                <>{error.message}</>
              </li>
            ))}
          </ul>
        </div>
      );
      Modal.error({
        title: "Validation Error",
        content: errorMessage,
        onOk() {},
      });
      return false;
    }

    setErrors([]);
    return true;
  };

  return (
    <>
      <Formik
        initialValues={Documents}
        onSubmit={(values) => {
          const isValid = checkValidation();
          if (isValid) {
            onFinish();
          }
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <div>
            <Table
              name="documents"
              rowKey={(row, index) => "" + index}
              size="small"
              pagination={false}
              style={{ width: "100%" }}
              dataSource={dataSource}
              columns={[
                {
                  title: "Document",
                  key: "document_title",
                  dataIndex: "document_title",
                  render: (text, record) => {
                    return (
                      <>
                        {text}
                        {record.required && (
                          <span
                            style={{
                              color: "#d54c4c",
                            }}
                          >
                            *
                          </span>
                        )}
                        <br />
                        {errors.includes(record.document_id) && (
                          <span
                            style={{
                              color: "#d54c4c",
                              fontSize: ".5rem",
                            }}
                          >
                            Only <b>PDF</b> files under <b>1 MB</b> are allowed
                          </span>
                        )}
                      </>
                    );
                  },
                },
                {
                  title: "Upload Document",
                  key: "document_url",
                  render: (text, i) => (
                    <>
                      <Upload
                        name={`documents.${i}.document_url`}
                        onRemove={(file) => {
                          const res = handleDelete(file, text.document_id);
                          return res;
                        }}
                        showUploadList={{
                          showRemoveIcon: true,
                          showDownloadIcon: true,
                          showPreviewIcon: true,
                        }}
                        fileList={listRender(
                          text.document_id,
                          fileList[text.document_id]
                        )}
                        multiple={true}
                        accept=".pdf"
                        maxCount={1}
                        beforeUpload={(file) => {
                          return checkUpload(file);
                        }}
                        customRequest={(option) => {
                          return handleUpload(text.document_id, option);
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          style={{ width: "100%" }}
                          disabled={Object.keys(fileList).includes(
                            text.document_id
                          )}
                        >
                          Click to Upload
                        </Button>

                        {/* <div
                          style={{
                            width: "100%",
                            display: "flex",
                            gap: "10px",
                            margin: "10px 0 0",
                          }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: "50%" }}
                            disabled={Object.keys(fileList).includes(
                              text.document_id
                            )}
                          >
                            View
                          </Button>
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: "50%" }}
                            disabled={Object.keys(fileList).includes(
                              text.document_id
                            )}
                          >
                            Remove
                          </Button>
                        </div> */}
                      </Upload>
                    </>
                  ),
                },
              ]}
            />
            <Row gutter={10} style={{ margin: "50px" }}>
              <Col>
                <Button type={"primary"} onClick={handleSubmit}>
                  Save
                </Button>
              </Col>
              {/* 
              <Col>
                <Button type={"success"} onClick={onFinish}>
                  Next
                </Button>
              </Col> */}
            </Row>
          </div>
        )}
      </Formik>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
